import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';

/**
 * Zuordnung "Feld -> Reiter", abgeleitet aus dem tatsaechlich gerenderten Aufbau statt
 * deklariert.
 *
 * Warum nicht deklariert: Bis Plan 20-16 stand die Zuordnung als handgepflegte Feldliste im
 * Unfalldaten-Formular (`ACCIDENT_TAB_FIELDS`). Sie musste bei jeder Feldaenderung
 * mitgezogen werden, und sie existierte nur fuer genau ein Formular. Die vier anderen
 * haetten je eine eigene gebraucht -- Formular D allein haette rund 280 Zeilen bedeutet.
 * Das ist genau die Duplikation, die Entscheidung D-05 dieser Phase abgeschafft hat, und
 * sie waere sofort auseinandergelaufen.
 *
 * Wie es stattdessen laeuft: Die geteilten Feldkomponenten (`validatedFields.tsx`) kennen
 * ihren Feldnamen ohnehin -- sie bekommen ihn als Prop. Beim Mounten melden sie ihn an die
 * Registry, zusammen mit dem Reiter, in dessen Panel sie gerendert werden
 * (`FieldTabScopeContext`). Ein Formular, das seine Panels in diesen Scope stellt, bekommt
 * die Reiterpunkte damit ohne eine einzige Feldangabe.
 *
 * Gespeichert wird bewusst nur das ERSTE Pfadsegment (`rotaryKilns` statt
 * `rotaryKilns.0.measurements.0.dustValue`): `tabsWithErrors` vergleicht ohnehin mit
 * Punkt-Grenze, das Segment deckt also alle darunterliegenden Pfade ab -- auch die von
 * Listeneintraegen, die gerade gar nicht ausgeklappt sind. Der Preis ist eine theoretische
 * Unschaerfe: laege dasselbe Wurzelfeld auf zwei Reitern, bekaemen beide einen Punkt. Das
 * kommt in den fuenf Formularen nicht vor (geprueft: kein Wurzelsegment tritt auf zwei
 * Reitern desselben Formulars auf).
 *
 * Beschriftungen (Phase 43, D-13): Neben der Reiterzuordnung fuehrt die Registry je
 * Wurzelfeld eine deutsche Beschriftung, damit die Anzeige "Folgende Felder wurden geaendert"
 * technische Namen des Backends (`changedFieldsSinceRevision`) in Klartext uebersetzen kann.
 * Die geteilten Feldkomponenten melden sie aus ihrem `label` bzw. `aria-label` mit an.
 * Listen melden ihre Beschriftung nur explizit an der Abschnittswurzel (D-12: eine Liste ist
 * ein Feld) -- Blattpfade wie `rotaryKilns.0.name` erzeugen keinen Beschriftungseintrag.
 * Formulare ohne Reiter melden Beschriftungen mit Reiter `null`.
 */

/** Reiterwert -> Wurzelsegmente der auf ihm gerenderten Felder. */
export type RegisteredTabFields = Record<string, string[]>;

/** Wurzelfeld -> Reiter (oder `null` ohne Reiter) und deutsche Beschriftung. */
export type RegisteredFieldLabels = Record<string, { tab: string | null; label: string }>;

export interface FieldTabRegistry {
  /**
   * Meldet ein Feld fuer einen Reiter (`null`: Formular ohne Reiter) und optional mit
   * Beschriftung an. Mehrfachaufrufe sind wirkungslos; die erste Beschriftung gewinnt.
   */
  register(tab: string | null, field: string, label?: string): void;
  /** Stabiler Schnappschuss der Beschriftungen; neue Identitaet nur bei Neuerung. */
  getLabels(): RegisteredFieldLabels;
  /** Stabiler Schnappschuss; neue Objektidentitaet nur, wenn wirklich etwas dazukam. */
  getMap(): RegisteredTabFields;
  subscribe(listener: () => void): () => void;
}

const EMPTY_MAP: RegisteredTabFields = {};
const EMPTY_LABELS: RegisteredFieldLabels = {};

export function createFieldTabRegistry(): FieldTabRegistry {
  const byTab = new Map<string, Set<string>>();
  const listeners = new Set<() => void>();
  let snapshot: RegisteredTabFields = EMPTY_MAP;
  const labelEntries = new Map<string, { tab: string | null; label: string }>();
  let labelSnapshot: RegisteredFieldLabels = EMPTY_LABELS;
  let notifyScheduled = false;

  // Gebuendelt benachrichtigen: beim Mounten eines Reiters melden sich alle seine
  // Felder in derselben Effektphase an. Ohne Buendelung liefe pro Feld ein eigener
  // Store-Durchlauf; mit ihr genau einer je Commit.
  const scheduleNotify = () => {
    if (notifyScheduled) return;
    notifyScheduled = true;
    queueMicrotask(() => {
      notifyScheduled = false;
      listeners.forEach((listener) => listener());
    });
  };

  return {
    register(tab: string | null, field: string, label?: string): void {
      const segment = field.split('.')[0];
      if (!segment) return;

      let changed = false;

      if (tab !== null) {
        let fields = byTab.get(tab);
        if (!fields) {
          fields = new Set<string>();
          byTab.set(tab, fields);
        }

        const sizeBefore = fields.size;
        fields.add(segment);
        if (fields.size !== sizeBefore) {
          const next: RegisteredTabFields = {};
          for (const [key, value] of byTab) next[key] = [...value];
          snapshot = next;
          changed = true;
        }
      }

      // Beschriftung nur fuer Wurzelfelder (kein Punkt), erste gewinnt.
      const trimmed = label?.trim();
      if (trimmed && field === segment && !labelEntries.has(segment)) {
        labelEntries.set(segment, { tab, label: trimmed });
        labelSnapshot = Object.fromEntries(labelEntries);
        changed = true;
      }

      if (changed) scheduleNotify();
    },
    getLabels: () => labelSnapshot,
    getMap: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** Die Registry der aktuell bearbeiteten Meldung. `null` ausserhalb eines Report-Formulars. */
export const FieldTabRegistryContext = createContext<FieldTabRegistry | null>(null);

/** Der Reiter, in dessen Panel gerade gerendert wird. `null` in Formularen ohne Reiter. */
export const FieldTabScopeContext = createContext<string | null>(null);

/**
 * Meldet ein Feld (und optional seine Beschriftung) beim Mounten an die Registry. Ohne
 * Registry-Kontext (Einzelnutzung) passiert nichts; ohne Reiter-Scope wird nur die
 * Beschriftung gefuehrt.
 */
export function useRegisterFieldTab(field: string, label?: string): void {
  const registry = useContext(FieldTabRegistryContext);
  const tab = useContext(FieldTabScopeContext);

  useEffect(() => {
    if (!registry) return;
    registry.register(tab, field, label);
    // Kein Cleanup: die Kenntnis "Feld X liegt auf Reiter Y" bleibt gueltig, auch wenn der
    // Reiter gerade nicht gerendert ist -- genau darauf beruht die Kennzeichnung nicht
    // sichtbarer Reiter.
  }, [registry, tab, field, label]);
}

const NOOP_SUBSCRIBE = () => () => {};
const EMPTY_SNAPSHOT = () => EMPTY_MAP;
const EMPTY_LABEL_SNAPSHOT = () => EMPTY_LABELS;

/** Liest die angemeldeten Beschriftungen und rendert neu, sobald neue dazukommen. */
export function useFieldLabelMap(): RegisteredFieldLabels {
  const registry = useContext(FieldTabRegistryContext);
  return useSyncExternalStore(
    registry ? registry.subscribe : NOOP_SUBSCRIBE,
    registry ? registry.getLabels : EMPTY_LABEL_SNAPSHOT
  );
}

/** Liest die aktuelle Zuordnung und rendert neu, sobald neue Felder dazukommen. */
export function useFieldTabMap(): RegisteredTabFields {
  const registry = useContext(FieldTabRegistryContext);
  return useSyncExternalStore(
    registry ? registry.subscribe : NOOP_SUBSCRIBE,
    registry ? registry.getMap : EMPTY_SNAPSHOT
  );
}

/**
 * Schliesst die einzige Luecke der Render-Ableitung: Reiter, die noch NIE gerendert wurden,
 * haben nichts angemeldet und koennten keinen Punkt tragen.
 *
 * Sobald `needed` (praktisch: es liegen Feldfehler an) zutrifft und noch nicht jeder Reiter
 * etwas angemeldet hat, liefert der Hook genau einmal `true` zurueck. Das Formular schaltet
 * damit `keepMounted` an, alle Panels rendern in einem Commit, ihre Felder melden sich an --
 * und der Effekt hier schaltet unmittelbar danach wieder ab. Die Panels verschwinden also
 * sofort wieder aus dem Baum, die Kenntnis bleibt.
 *
 * `keepMountedMode="display-none"` gehoert zwingend dazu (Mantine 9 / React 19): der
 * Standardmodus rendert nicht aktive Panels in einem `<Activity mode="hidden">`, und in
 * einer versteckten Activity laufen die Effekte NICHT -- die Anmeldung fiele damit aus.
 *
 * Der `done`-Riegel macht den Vorgang einmalig. Ohne ihn bliebe `keepMounted` dauerhaft an,
 * sobald ein Fehlerschluessel zu gar keinem Reiter gehoert (formularweite Regeln, berechnete
 * Traegerfelder) oder ein Reiter ueberhaupt keine Felder traegt.
 */
export function useTabFieldDiscovery(tabValues: readonly string[], needed: boolean): boolean {
  const map = useFieldTabMap();
  const [done, setDone] = useState(false);

  const complete = tabValues.every((tab) => (map[tab]?.length ?? 0) > 0);
  const discovering = needed && !done && !complete;

  useEffect(() => {
    // Laeuft NACH den Anmelde-Effekten der frisch gemounteten Felder: React arbeitet
    // Effekte von innen nach aussen ab, und dieser Hook haengt im Formular oberhalb der
    // Panels.
    if (discovering) setDone(true);
  }, [discovering]);

  return discovering;
}
